import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, DollarSign, MessageCircle, Target, Filter,
  RefreshCw, ChevronLeft, ChevronRight, Calendar, Eye, Users, Globe, MousePointerClick
} from 'lucide-react';
import { api } from '../../src/services/api';
import { MarketingMetaDailyMetric } from '../../types';
import { computeByDay, aggregateKpis } from '../../src/lib/marketing/agregacaoDiaria';
import MultiTooltip from '../../src/components/marketing/MultiTooltip';
import AnaliseMetricasChart from './charts/AnaliseMetricasChart';
import ResultadosPorDiaChart from './charts/ResultadosPorDiaChart';

// ─── helpers ────────────────────────────────────────────────────
const fmt = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const fmtShort = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => v.toLocaleString('pt-BR');
const fmtDate = (d: string) => { const [, m, day] = d.split('-'); return `${day}/${m}`; };
const fmtFull = (d: string) => { if (!d) return '—'; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };
const addDays = (dateStr: string, n: number) => {
  const d = new Date(dateStr + 'T12:00:00'); d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};
const currentMonthRange = () => {
  const now = new Date();
  const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  return { first, last };
};

// ─── Comparison period ────────────────────────────────────────
const getCompPeriod = (startDate: string, endDate: string) => {
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  const periodMs = end.getTime() - start.getTime();
  const compEnd = new Date(start.getTime() - 86400000);
  const compStart = new Date(compEnd.getTime() - periodMs);
  return {
    start: compStart.toISOString().split('T')[0],
    end: compEnd.toISOString().split('T')[0],
  };
};

const pctChange = (current: number, previous: number): number | null => {
  if (previous === 0 && current === 0) return 0;
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

// ─── linear regression ─────────────────────────────────────────
function linearRegression(values: number[]) {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] ?? 0 };
  const sumX = (n * (n - 1)) / 2;
  const sumY = values.reduce((a, b) => a + b, 0);
  const sumXY = values.reduce((s, y, x) => s + x * y, 0);
  const sumX2 = values.reduce((s, _, x) => s + x * x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  return { slope, intercept: (sumY - slope * sumX) / n };
}

// ─── Calendar ──────────────────────────────────────────────────
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                   'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DAYS_SHORT = ['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'];

const CalendarMonth: React.FC<{
  year: number; month: number;
  tempStart: string; tempEnd: string; hoverDate: string;
  onSelect: (d: string) => void; onHover: (d: string) => void;
}> = ({ year, month, tempStart, tempEnd, hoverDate, onSelect, onHover }) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const todayStr = today();
  const rangeEnd = tempEnd || hoverDate;
  const [rangeS, rangeE] = tempStart && rangeEnd
    ? (tempStart <= rangeEnd ? [tempStart, rangeEnd] : [rangeEnd, tempStart])
    : ['', ''];
  const cells: (string | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

  return (
    <div className="p-3 w-60">
      <p className="text-xs font-semibold text-center text-gray-700 dark:text-gray-200 mb-2">{MONTHS_PT[month]} {year}</p>
      <div className="grid grid-cols-7 mb-1">
        {DAYS_SHORT.map(d => <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={`e${i}`} className="h-7" />;
          const isStart = dateStr === tempStart, isEnd = dateStr === (tempEnd || hoverDate);
          const inRange = !!(rangeS && rangeE && dateStr > rangeS && dateStr < rangeE);
          return (
            <button key={dateStr} onMouseEnter={() => onHover(dateStr)} onClick={() => onSelect(dateStr)}
              className={[
                'h-7 w-7 text-xs font-medium flex items-center justify-center mx-auto transition-all select-none',
                isStart || isEnd ? 'rounded-full bg-primary text-white shadow-sm' :
                inRange ? 'bg-primary/15 text-primary dark:text-green-300 rounded-none' :
                'rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
                dateStr === todayStr && !isStart && !isEnd ? 'ring-1 ring-primary ring-offset-1' : ''
              ].join(' ')}>
              {parseInt(dateStr.split('-')[2])}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateRangePicker: React.FC<{
  startDate: string; endDate: string;
  onChange: (s: string, e: string) => void;
  triggerLabel?: string;
  align?: 'left' | 'right';
}> = ({ startDate, endDate, onChange, triggerLabel, align = 'left' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [hoverDate, setHoverDate] = useState('');
  const [phase, setPhase] = useState<'start' | 'end'>('start');
  const [viewYear, setViewYear] = useState(() => new Date((startDate || today()) + 'T12:00:00').getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date((startDate || today()) + 'T12:00:00').getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
    if (isOpen) document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isOpen]);
  useEffect(() => { setTempStart(startDate); setTempEnd(endDate); }, [startDate, endDate]);

  const open = () => {
    const d = new Date((startDate || today()) + 'T12:00:00');
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
    setTempStart(startDate); setTempEnd(endDate); setPhase('start'); setIsOpen(true);
  };
  const handleSelect = (dateStr: string) => {
    if (phase === 'start' || dateStr < tempStart) { setTempStart(dateStr); setTempEnd(''); setPhase('end'); }
    else { setTempEnd(dateStr); setPhase('start'); }
  };
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
  const month2 = viewMonth === 11 ? 0 : viewMonth + 1;
  const year2 = viewMonth === 11 ? viewYear + 1 : viewYear;
  const dayCount = tempStart && tempEnd
    ? Math.round((new Date(tempEnd + 'T12:00:00').getTime() - new Date(tempStart + 'T12:00:00').getTime()) / 86400000) + 1
    : null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={open} className={triggerLabel
        ? 'text-[11px] font-semibold text-primary hover:underline'
        : 'flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors'
      }>
        {triggerLabel ? triggerLabel : (
          <>
            <Calendar size={13} />
            <span className="font-semibold">{fmtFull(startDate)}</span>
            <span className="text-gray-400">→</span>
            <span className="font-semibold">{fmtFull(endDate)}</span>
          </>
        )}
      </button>
      {isOpen && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl`}>
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronLeft size={15} /></button>
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{phase === 'start' ? 'Clique para definir o início' : 'Clique para definir o fim'}</span>
            <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronRight size={15} /></button>
          </div>
          <div className="flex divide-x divide-gray-100 dark:divide-gray-700" onMouseLeave={() => setHoverDate('')}>
            <CalendarMonth year={viewYear} month={viewMonth} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
            <CalendarMonth year={year2} month={month2} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500">{dayCount ? `${dayCount} dias selecionados` : 'Selecione o período'}</span>
            <div className="flex gap-2">
              <button onClick={() => { setTempStart(''); setTempEnd(''); setPhase('start'); }} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Limpar</button>
              <button onClick={() => { if (tempStart && tempEnd) { onChange(tempStart, tempEnd); setIsOpen(false); } }} disabled={!tempStart || !tempEnd} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-lg disabled:opacity-40 hover:bg-primary/90">Aplicar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ChangeBadge ───────────────────────────────────────────────
const ChangeBadge: React.FC<{ pct: number | null; lowerIsBetter?: boolean }> = ({ pct, lowerIsBetter = false }) => {
  if (pct === null) return null;
  const isPositive = lowerIsBetter ? pct < 0 : pct > 0;
  const isNeutral = pct === 0;
  return (
    <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
      isNeutral ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' :
      isPositive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
      'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
    }`}>
      {pct > 0 ? `+${pct}%` : `${pct}%`}
    </span>
  );
};

// ─── KPI card ──────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon: Icon, color, badge }: {
  label: string; value: string; sub?: string; icon: any; color: string;
  badge?: { pct: number | null; lowerIsBetter?: boolean };
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-4">
    <div className={`p-2.5 rounded-lg ${color} flex-shrink-0`}><Icon size={20} className="text-white" /></div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        {badge && <ChangeBadge pct={badge.pct} lowerIsBetter={badge.lowerIsBetter} />}
      </div>
      <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Result type mini-card ─────────────────────────────────────
const ResultCard = ({ icon: Icon, label, description, value, color, pct, badge }: {
  icon: any; label: string; description: string; value: number; color: string; pct: number;
  badge?: { pct: number | null; lowerIsBetter?: boolean };
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3">
    <div className={`p-2 rounded-lg ${color} flex-shrink-0`}><Icon size={16} className="text-white" /></div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">{label}</p>
        {badge && <ChangeBadge pct={badge.pct} lowerIsBetter={badge.lowerIsBetter} />}
      </div>
      <p className="text-[10px] text-gray-400 dark:text-gray-500">{description}</p>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-base font-bold text-gray-900 dark:text-white">{value.toLocaleString('pt-BR')}</p>
      <p className="text-[10px] text-gray-400">{pct}% do total</p>
    </div>
  </div>
);


// ─── Main component ────────────────────────────────────────────
const MetaDashboard: React.FC = () => {
  const [metrics, setMetrics]               = useState<MarketingMetaDailyMetric[]>([]);
  const [compMetrics, setCompMetrics]       = useState<MarketingMetaDailyMetric[]>([]);
  const [allCampaigns, setAllCampaigns]     = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [startDate, setStartDate]           = useState(daysAgo(29));
  const [endDate, setEndDate]               = useState(today());
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [campaignOpen, setCampaignOpen]     = useState(false);
  const [customCompPeriod, setCustomCompPeriod] = useState<{ start: string; end: string } | null>(null);

  const { first: cmFirst, last: cmLast } = currentMonthRange();
  const FORECAST_DAYS = 7;

  const compPeriod       = useMemo(() => getCompPeriod(startDate, endDate), [startDate, endDate]);
  const activeCompPeriod = useMemo(() => customCompPeriod ?? compPeriod, [customCompPeriod, compPeriod]);

  const loadData = async () => {
    setLoading(true); setError(null);
    try {
      const [data, campaigns, compData] = await Promise.all([
        api.marketing.getMetrics(startDate, endDate),
        api.marketing.getCampaigns(),
        api.marketing.getMetrics(compPeriod.start, compPeriod.end),
      ]);
      setMetrics(data);
      setAllCampaigns(campaigns);
      setCompMetrics(compData);
      if (selectedCampaigns.length === 0) setSelectedCampaigns(campaigns);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar dados.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    setCustomCompPeriod(null); // reset custom comparison when main period changes
    loadData();
  }, [startDate, endDate]);

  // When user picks a custom comparison period, fetch that data separately
  useEffect(() => {
    if (!customCompPeriod) return;
    api.marketing.getMetrics(customCompPeriod.start, customCompPeriod.end)
      .then(setCompMetrics)
      .catch(e => setError(e.message));
  }, [customCompPeriod]);

  const isRangeActive = (days: number) => startDate === daysAgo(days - 1) && endDate === today();
  const isCurrentMonthActive = startDate === cmFirst && endDate === cmLast;

  const filtered     = useMemo(() => metrics.filter(m => selectedCampaigns.includes(m.campaign_name)), [metrics, selectedCampaigns]);
  const compFiltered = useMemo(() => compMetrics.filter(m => selectedCampaigns.includes(m.campaign_name)), [compMetrics, selectedCampaigns]);

  const byDay     = useMemo(() => computeByDay(filtered),     [filtered]);
  const compByDay = useMemo(() => computeByDay(compFiltered), [compFiltered]);

  const kpis     = useMemo(() => aggregateKpis(byDay),     [byDay]);
  const compKpis = useMemo(() => aggregateKpis(compByDay), [compByDay]);

  // ─── Forecast ─────────────────────────────────────────────
  const forecastData = useMemo(() => {
    if (byDay.length < 3) return [];
    const { slope, intercept } = linearRegression(byDay.map(d => d.spend));
    const lastDate = byDay[byDay.length - 1].date;
    return Array.from({ length: FORECAST_DAYS }, (_, i) => ({
      date: addDays(lastDate, i + 1),
      projected_spend: Math.max(0, parseFloat((slope * (byDay.length + i) + intercept).toFixed(2))),
    }));
  }, [byDay]);

  const spendChartData = useMemo(() => {
    const actual = byDay.map((d, i) => ({
      date: fmtDate(d.date), spend: d.spend,
      projected_spend: i === byDay.length - 1 && forecastData.length > 0 ? d.spend : undefined,
    }));
    const projected = forecastData.map(d => ({
      date: fmtDate(d.date), spend: undefined as number | undefined,
      projected_spend: d.projected_spend,
    }));
    return [...actual, ...projected];
  }, [byDay, forecastData]);

  const byCampaign = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of filtered) map.set(m.campaign_name, (map.get(m.campaign_name) ?? 0) + (Number(m.spend) || 0));
    return Array.from(map.entries())
      .map(([name, spend]) => ({ name: name.length > 28 ? name.slice(0, 26) + '…' : name, spend: parseFloat(spend.toFixed(2)) }))
      .sort((a, b) => b.spend - a.spend);
  }, [filtered]);

  const toggleCampaign = (name: string) =>
    setSelectedCampaigns(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);

  const multiResultTypes = [kpis.totalConversations > 0, kpis.totalLeads > 0, kpis.totalLandingViews > 0].filter(Boolean).length;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Carregando dados de campanhas...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">

      {/* ── Filtros ───────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            {([7, 15, 30, 90] as const).map(d => (
              <button key={d} onClick={() => { setStartDate(daysAgo(d - 1)); setEndDate(today()); }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${isRangeActive(d) ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>{d}d</button>
            ))}
            <button onClick={() => { setStartDate(cmFirst); setEndDate(cmLast); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${isCurrentMonthActive ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>Mês atual</button>
          </div>

          <DateRangePicker startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />

          <div className="relative">
            <button onClick={() => setCampaignOpen(!campaignOpen)} className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
              <Filter size={13} />
              Campanhas
              {selectedCampaigns.length < allCampaigns.length && <span className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{selectedCampaigns.length}</span>}
            </button>
            {campaignOpen && (
              <div className="absolute left-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl min-w-[240px] p-2">
                <div className="flex justify-between items-center px-2 pb-2 border-b border-gray-100 dark:border-gray-700 mb-1">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Filtrar campanhas</span>
                  <button onClick={() => setSelectedCampaigns(selectedCampaigns.length === allCampaigns.length ? [] : [...allCampaigns])} className="text-[11px] text-primary hover:underline font-medium">{selectedCampaigns.length === allCampaigns.length ? 'Desmarcar todas' : 'Marcar todas'}</button>
                </div>
                {allCampaigns.map(name => (
                  <label key={name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <input type="checkbox" checked={selectedCampaigns.includes(name)} onChange={() => toggleCampaign(name)} className="rounded accent-primary" />
                    <span className="text-xs text-gray-700 dark:text-gray-200 truncate max-w-[190px]">{name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button onClick={loadData} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <RefreshCw size={12} />Atualizar
          </button>
        </div>

        {/* Comparison period row */}
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {customCompPeriod ? 'Comparando com:' : 'Comparando com período anterior:'}
          </span>
          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
            {fmtFull(activeCompPeriod.start)} → {fmtFull(activeCompPeriod.end)}
          </span>
          {customCompPeriod && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              personalizado
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <DateRangePicker
              startDate={activeCompPeriod.start}
              endDate={activeCompPeriod.end}
              onChange={(s, e) => setCustomCompPeriod({ start: s, end: e })}
              triggerLabel="Alterar período"
              align="right"
            />
            {customCompPeriod && (
              <button
                onClick={() => setCustomCompPeriod(null)}
                className="text-[11px] text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                ↩ Automático
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">{error}</div>}

      {/* ── KPI Cards ─────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          label="Investimento" value={fmt(kpis.totalSpend)} icon={DollarSign} color="bg-primary"
          sub={`${byDay.length} dias`}
          badge={{ pct: pctChange(kpis.totalSpend, compKpis.totalSpend) }}
        />
        <KpiCard
          label="Impressões" value={fmtNum(kpis.totalImpressions)} icon={Eye} color="bg-indigo-500"
          sub="total no período"
          badge={{ pct: pctChange(kpis.totalImpressions, compKpis.totalImpressions) }}
        />
        <KpiCard
          label="Cliques no Link" value={fmtNum(kpis.totalLinkClicks)} icon={MousePointerClick} color="bg-teal-500"
          sub="total no período"
          badge={{ pct: pctChange(kpis.totalLinkClicks, compKpis.totalLinkClicks) }}
        />
        <KpiCard
          label="Resultados" value={fmtNum(kpis.totalResults)} icon={Target} color="bg-blue-500"
          sub={`${fmtNum(kpis.totalConversations)} conv. · ${fmtNum(kpis.totalLeads)} leads · ${fmtNum(kpis.totalLandingViews)} LP`}
          badge={{ pct: pctChange(kpis.totalResults, compKpis.totalResults) }}
        />
        <KpiCard
          label="CPM Médio" value={fmt(kpis.avgCpm)} icon={Eye} color="bg-purple-500"
          sub="custo por mil impressões"
          badge={{ pct: pctChange(kpis.avgCpm, compKpis.avgCpm), lowerIsBetter: true }}
        />
        <KpiCard
          label="Custo por Resultado" value={kpis.avgCostPerResult > 0 ? fmt(kpis.avgCostPerResult) : '—'} icon={TrendingUp} color="bg-amber-500"
          sub="média do período"
          badge={{ pct: pctChange(kpis.avgCostPerResult, compKpis.avgCostPerResult), lowerIsBetter: true }}
        />
      </div>

      {/* ── Breakdown de Resultados ───────────────────── */}
      {byDay.length > 0 && multiResultTypes > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Composição dos Resultados</h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">total: {fmtNum(kpis.totalResults)}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {kpis.totalConversations > 0 && (
              <ResultCard icon={MessageCircle} color="bg-blue-500"
                label="Conversas Iniciadas" description="Mensagens WhatsApp iniciadas pelo anúncio"
                value={kpis.totalConversations} pct={kpis.totalResults > 0 ? Math.round(kpis.totalConversations / kpis.totalResults * 100) : 0}
                badge={{ pct: pctChange(kpis.totalConversations, compKpis.totalConversations) }}
              />
            )}
            {kpis.totalLeads > 0 && (
              <ResultCard icon={Users} color="bg-purple-500"
                label="Leads / Cadastros" description="Formulários preenchidos e conversões de pixel"
                value={kpis.totalLeads} pct={kpis.totalResults > 0 ? Math.round(kpis.totalLeads / kpis.totalResults * 100) : 0}
                badge={{ pct: pctChange(kpis.totalLeads, compKpis.totalLeads) }}
              />
            )}
            {kpis.totalLandingViews > 0 && (
              <ResultCard icon={Globe} color="bg-amber-500"
                label="Visitas à Página" description="Acessos à landing page após o clique"
                value={kpis.totalLandingViews} pct={kpis.totalResults > 0 ? Math.round(kpis.totalLandingViews / kpis.totalResults * 100) : 0}
                badge={{ pct: pctChange(kpis.totalLandingViews, compKpis.totalLandingViews) }}
              />
            )}
          </div>
        </div>
      )}

      {byDay.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <p className="text-gray-400 dark:text-gray-500">Nenhum dado encontrado para o período e filtros selecionados.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Os dados são importados diariamente pelo n8n às 6h.</p>
        </div>
      ) : (
        <>
          {/* ── Gráfico multi-métrica ──────────────────── */}
          <AnaliseMetricasChart byDay={byDay} />

          {/* ── Projeção ───────────────────────────────── */}
          {forecastData.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-0.5">Projeção de Gasto</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Estimativa para os próximos {FORECAST_DAYS} dias baseada na tendência do período</p>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={spendChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
                  <YAxis tickFormatter={v => fmtShort(v)} tick={{ fontSize: 10 }} tickLine={false} width={70} />
                  <Tooltip content={<MultiTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="spend" name="Gasto Real" stroke="#5F6C37" strokeWidth={2} dot={false} connectNulls={false} />
                  <Line type="monotone" dataKey="projected_spend" name="Projeção" stroke="#F6A937" strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Resultados por dia + Gasto por campanha ─ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ResultadosPorDiaChart byDay={byDay} kpis={kpis} />

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-4">Gasto por Campanha</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byCampaign} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tickFormatter={v => fmtShort(v)} tick={{ fontSize: 10 }} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} tickLine={false} width={130} />
                  <Tooltip content={<MultiTooltip />} />
                  <Bar dataKey="spend" name="Gasto Total" fill="#5F6C37" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetaDashboard;
